import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import nurseryQuarantineService from '@/services/nurseryQuarantine';
import type { LotQuarantineEntry, QuarantineTreatmentState } from '@/types/nurseryQuarantine';
import { useOrgTimezone, formatLocalDateInTz, formatDateTimeInTz } from '@/utils/timezone';
import { cn } from '@/utils/cn';

const STATE_TEXT: Record<QuarantineTreatmentState, string> = {
  cancelled: 'Cancelled',
  awaiting_spray: 'Not sprayed yet',
  awaiting_validation: 'Sprayed, not validated',
  treated: 'Treated',
  expired: 'Treated, window passed',
};

const STATE_STYLE: Record<QuarantineTreatmentState, string> = {
  cancelled: 'bg-neutral-100 text-neutral-500',
  awaiting_spray: 'bg-sky-50 text-sky-800',
  awaiting_validation: 'bg-amber-50 text-amber-800',
  treated: 'bg-green-50 text-green-800',
  expired: 'bg-neutral-100 text-neutral-700',
};

/**
 * The quarantine treatments this lot's trees went out under (ICA plan,
 * Phase 4): the protocol, the load, how many of this lot's trees, the orders
 * and their PHAC numbers, and where the treatment stands. Read through the
 * picks, so it lists only loads trees were actually picked from this lot for.
 * Renders nothing for a lot that never went out under a protocol.
 */
function LotQuarantineHistory({ lotId, canOpenSprays }: { lotId: number; canOpenSprays: boolean }) {
  const tz = useOrgTimezone();
  const [entries, setEntries] = useState<LotQuarantineEntry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    nurseryQuarantineService.getLotQuarantine(lotId)
      .then((res) => { if (!cancelled) setEntries(res.success ? res.data ?? [] : []); })
      .catch(() => { if (!cancelled) setEntries([]); });
    return () => { cancelled = true; };
  }, [lotId]);

  if (!entries || entries.length === 0) return null;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="h-5 w-5 text-primary-600" />
        <h3 className="text-sm font-semibold text-neutral-800">Quarantine treatments</h3>
      </div>
      <ul className="divide-y divide-neutral-100">
        {entries.map((e) => (
          <li key={e.id} className="py-3 space-y-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
              <span className="min-w-0 break-words text-sm font-medium text-neutral-800">
                {`${e.code}${e.method === 'run_off' ? ' (run-off)' : e.method === 'pots' ? ' (pots)' : ''} · `}
                <Link to={`/nursery/loads/${e.load_id}`} className="text-primary-700 hover:underline">{`Load ${e.load_number}`}</Link>
                {` · ${e.trees} tree${e.trees === 1 ? '' : 's'}`}
              </span>
              <span className={cn('text-xs font-medium rounded-full px-2 py-0.5', STATE_STYLE[e.state])}>{STATE_TEXT[e.state]}</span>
            </div>
            <p className="text-xs text-neutral-500 break-words">
              {[
                e.completed_at ? `Sprayed ${formatDateTimeInTz(e.completed_at, tz)}` : `Despatch ${formatLocalDateInTz(e.despatch_date, tz)}`,
                e.area_name,
                e.validated_by_name ? `validated by ${e.validated_by_name}` : null,
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="text-xs text-neutral-600 break-words">
              {e.orders.map((o) => `${o.order_number}${o.phac_number ? ` (PHAC ${o.phac_number})` : ''}`).join(', ')}
            </p>
            {e.spray_application_id && canOpenSprays && (
              <Link to={`/spray/${e.spray_application_id}`} className="text-xs text-primary-700 hover:underline">
                {e.reference_number ?? 'Open the spray'}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default LotQuarantineHistory;
